import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

function Suggestions() {
  const [suggestion, setSuggestion] = useState('');
  const [businessId, setBusinessId] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBusiness = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: business } = await supabase
        .from('businesses')
        .select('id, business_name')
        .eq('owner_email', user.email)
        .single();
      if (business) {
        setBusinessId(business.id);
        setBusinessName(business.business_name || '');
      }
    };
    fetchBusiness();
  }, []);

  const handleSubmit = async () => {
    if (!suggestion.trim()) { setError('Please enter a suggestion before submitting.'); return; }
    setSubmitting(true);
    setError('');

    const { error: insertError } = await supabase.from('suggestions').insert({
      business_id: businessId || null,
      business_name: businessName || null,
      suggestion: suggestion.trim(),
    });

    if (insertError) {
      setError('Unable to submit your suggestion. Please try again.');
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    setSubmitted(true);
    setSuggestion('');
  };

  const handleAnother = () => {
    setSubmitted(false);
  };

  const examples = [
    '🚗 Capture vehicle info like year, make, model, and VIN number',
    '📅 Let customers book appointments directly through the chat',
    '⭐ Ask customers to leave a Google review after their service',
    '🔔 Send follow-up texts a few days after a job is done',
    '📊 Show me monthly reports on how many leads I captured',
    '💬 Let me see the full text conversation in the dashboard',
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: '32px', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>

        <header style={{ marginBottom: '32px' }}>
          <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '32px', fontWeight: 800, color: '#0C1A2E', letterSpacing: '-0.5px' }}>
            Suggestions
          </h1>
          <p style={{ marginTop: '6px', color: '#5A6B84', fontSize: '14px', lineHeight: 1.6 }}>
            We're always working to make ReplyFirst more useful for your business.
          </p>
        </header>

        {/* Intro card */}
        <div style={{ backgroundColor: '#0C1A2E', borderRadius: '14px', padding: '24px 28px', marginBottom: '24px', border: '1px solid #1A2744' }}>
          <div style={{ fontSize: '28px', marginBottom: '12px' }}>💡</div>
          <h2 style={{ margin: '0 0 10px', fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: 800, color: '#fff' }}>
            Help us build what you need
          </h2>
          <p style={{ margin: 0, fontSize: '14px', color: '#7A8EA8', lineHeight: 1.7 }}>
            We're always updating ReplyFirst to make it as useful as possible for auto shops like yours. Whether it's improvements to how the AI handles conversations, new features you'd like to see, or things that would save you time — we want to hear it. Every suggestion goes directly to our team.
          </p>
        </div>

        {/* Examples */}
        <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', padding: '20px 24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(26,86,219,0.06)' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0C1A2E', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Ideas to get you thinking
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {examples.map((example, i) => (
              <div
                key={i}
                onClick={() => { setSuggestion(example.slice(2).trim()); setSubmitted(false); }}
                style={{ padding: '10px 14px', backgroundColor: '#F4F7FF', borderRadius: '8px', border: '1px solid #DDE3F0', fontSize: '14px', color: '#3D4F66', cursor: 'pointer', transition: 'background 0.15s', lineHeight: 1.5 }}
              >
                {example}
              </div>
            ))}
          </div>
          <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#9AABBE' }}>
            Click any idea above to use it as a starting point, or write your own below.
          </p>
        </div>

        {/* Submission form */}
        <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', padding: '24px', boxShadow: '0 2px 8px rgba(26,86,219,0.06)' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🙌</div>
              <h3 style={{ margin: '0 0 8px', fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: '#0C1A2E' }}>
                Thank you!
              </h3>
              <p style={{ fontSize: '14px', color: '#5A6B84', lineHeight: 1.7, marginBottom: '20px' }}>
                Your suggestion has been sent directly to our team. We read every one and use them to decide what to build next.
              </p>
              <button
                onClick={handleAnother}
                style={{ padding: '10px 24px', borderRadius: '999px', border: 'none', backgroundColor: '#F97316', color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Submit another suggestion
              </button>
            </div>
          ) : (
            <>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0C1A2E', marginBottom: '8px' }}>
                Your suggestion
              </label>
              <textarea
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                placeholder="e.g. I'd love it if the AI could ask for the customer's vehicle make and model so I have that info before calling them back..."
                rows={5}
                style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #DDE3F0', fontSize: '14px', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0C1A2E', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 }}
              />
              {error && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#B91C1C' }}>{error}</div>
              )}
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !suggestion.trim()}
                  style={{ padding: '11px 28px', borderRadius: '999px', border: 'none', backgroundColor: submitting || !suggestion.trim() ? '#DDE3F0' : '#F97316', color: submitting || !suggestion.trim() ? '#9AABBE' : '#fff', fontSize: '14px', fontWeight: 700, cursor: submitting || !suggestion.trim() ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'background 0.15s' }}
                >
                  {submitting ? 'Sending…' : 'Send suggestion →'}
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

export default Suggestions;